import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  children: React.ReactNode;
  fullWidth?: boolean;
}

/**
 * Shared family-tree button styles used by forms, dialogs, and page actions.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      children,
      fullWidth = false,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    // FT-265: Do not use whitespace-nowrap — translated labels can be ~40% longer
    // than English. Allow wrapping so text is never clipped in any locale.
    const baseClasses =
      'inline-flex items-center justify-center gap-2 rounded-xl border text-sm font-medium transition focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-60';

    const variantClasses = {
      primary:
        'border-transparent bg-[#185FA5] text-white hover:bg-[#0C447C] focus-visible:ring-[rgba(24,95,165,0.18)] disabled:bg-[#9E9890]',
      secondary:
        'border-[#D7D3CC] bg-[#F3EEE7] text-[#4A443E] hover:bg-[#ECE5DB] focus-visible:ring-[rgba(107,101,96,0.12)]',
      outline:
        'border-[#C8C3BB] bg-[#FFFFFF] text-[#1A1714] hover:bg-[#F9F7F4] focus-visible:ring-[rgba(107,101,96,0.12)]',
      ghost:
        'border-transparent bg-transparent text-[#6B6560] hover:bg-[#EEF4FB] hover:text-[#185FA5] focus-visible:ring-[rgba(24,95,165,0.12)]',
      danger:
        'border-transparent bg-[#C64535] text-white hover:bg-[#A93325] focus-visible:ring-[rgba(198,69,53,0.16)] disabled:bg-[#CDA39D]',
    };

    // FT-265: Use min-h instead of h so the button can grow when translated
    // text wraps to a second line (common with German compound nouns).
    const sizeClasses = {
      sm: 'min-h-9 px-3.5 py-1.5 text-sm',
      md: 'min-h-11 px-4 py-2 text-sm',
      lg: 'min-h-[56px] px-6 py-3 text-base',
    };

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${
          fullWidth ? 'w-full' : ''
        } ${className || ''}`}
        {...props}
      >
        {isLoading && (
          <svg
            className="w-4 h-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
